import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { developerOverride, getMockUser } from '@/utils/developerConfig';
import type { User, Session } from '@supabase/supabase-js';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (developerOverride) {
      console.log('[DEV OVERRIDE] Injecting mock user');
      setUser(getMockUser());
      setLoading(false);
      setReady(true);
      return;
    }

    // onAuthStateChange fires immediately with INITIAL_SESSION — no need for
    // getSession(), which would compete with useAuth.js for the Navigator lock.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[AUTH] State change event:', event, 'Session:', !!session);
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        setTimeout(() => setReady(true), 100);
      }
    );

    // Cleanup returned directly from useEffect so it actually runs on unmount.
    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      setUser(null);
      setSession(null);
    }
    return { error };
  };

  return { 
    user, 
    session, 
    loading, 
    ready,
    signIn, 
    signUp, 
    signOut 
  };
};
